/**
 * Classification markings (CAPCO portion marks) as the forms handle them.
 *
 * A marking is ONE string, exactly as stored on a `{ lang, value, capco }`
 * text: `U`, `TS`, `S//NF`. Nothing here parses it into parts or rebuilds it;
 * the only thing read out of it is the classification level, for the colour
 * bar. A stored marking is never rewritten by this module.
 *
 * This file imports nothing from gui-react, so it can move to `@brew/ui`
 * unchanged with the two components beside it.
 */

export type MarkingLevel = 'U' | 'C' | 'S' | 'TS';

export interface MarkingOption {
  /** The portion mark, which is also the stored value. */
  value: string;
  /** The level written out, e.g. `SECRET`. */
  title: string;
  level: MarkingLevel;
}

/** The markings the control offers. The server reads exactly these four levels. */
export const MARKING_OPTIONS: readonly MarkingOption[] = [
  { value: 'U', title: 'UNCLASSIFIED', level: 'U' },
  { value: 'C', title: 'CONFIDENTIAL', level: 'C' },
  { value: 'S', title: 'SECRET', level: 'S' },
  { value: 'TS', title: 'TOP SECRET', level: 'TS' },
];

/** The marking a new text starts with, and what an absent marking is read as. */
export const DEFAULT_MARKING = 'U';

/**
 * The classification level of a marking: the part before the first `//`.
 * `null` when it is not one of the four portion marks. An unknown marking has
 * NO level; it is never read as unclassified.
 */
export function markingLevel(marking: string): MarkingLevel | null {
  const head = marking.split('//')[0].trim();
  return head === 'U' || head === 'C' || head === 'S' || head === 'TS' ? head : null;
}

/** True when `marking` is one of `options`' values. */
export function isListedMarking(
  marking: string,
  options: readonly MarkingOption[] = MARKING_OPTIONS,
): boolean {
  return options.some((o) => o.value === marking);
}

/** `U · UNCLASSIFIED`: the mark and its title, so the meaning is in the text. */
export function markingOptionText(option: MarkingOption): string {
  return `${option.value} · ${option.title}`;
}

/** The option text of a stored marking the list does not offer. */
export function unlistedMarkingText(marking: string): string {
  return `${marking} · stored value, not in the list`;
}

/**
 * The full form of a marking, for the tooltip and the accessible name:
 * `Classification: CONFIDENTIAL`. A marking outside the list has no known
 * full form, so it is named exactly as stored (`Classification: S//NF`);
 * nothing here expands or rebuilds it.
 */
export function portionMarkTitle(
  marking: string,
  options: readonly MarkingOption[] = MARKING_OPTIONS,
): string {
  const listed = options.find((o) => o.value === marking);
  return `Classification: ${listed ? listed.title : marking}`;
}
